import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { delay } from '@icp/utils';
import { restApi } from '@icp/settings';
import { fileStorageState } from '../initialState';

const selectThis = (state) => state.fileStorage;

export const selectPermissionLoading = (permissionId) =>
  createSelector(selectThis, (state) => {
    if (!permissionId) {
      return state.permissionLoading;
    }
    return state.workspaceById[permissionId]?.permissionLoading;
  });
// export const selectSessionId = createSelector(selectThis, (state) => state.sessionId);
export const selectAuthentication = (permissionId) =>
  createSelector(selectThis, (state) => {
    if (!permissionId) {
      return state.authentication;
    }
    return state.workspaceById[permissionId]?.authentication;
  });
export const selectBaseUrl = (permissionId) =>
  createSelector(selectThis, (state) => {
    if (!permissionId) {
      return state.baseUrl;
    }
    return state.workspaceById[permissionId]?.baseUrl;
  });
export const selectSessionId = (permissionId) =>
  createSelector(selectThis, (state) => {
    if (!permissionId) {
      return state.sessionId;
    }
    return state.workspaceById[permissionId]?.sessionId;
  });

export const fetchUploadPermission = createAsyncThunk(
  'uploadPermission/fetchUploadPermission',
  async () => {
    return restApi.post('/form/api/form-entity-resource/workspace');
  },
  {
    condition: (payload, { getState }) => {
      const permissionLoading = selectPermissionLoading(payload.permissionId)(getState());
      return !permissionLoading;
    },
  },
);

export const uploadFile = (files, config, permissionId) => async (dispatch, getState) => {
  let sessionId = selectSessionId(permissionId)(getState());
  let baseUrl = selectBaseUrl(permissionId)(getState());
  let authentication = selectAuthentication(permissionId)(getState());

  if (!baseUrl) {
    const permissionLoading = selectPermissionLoading(permissionId)(getState());
    // 正在请求permission时，不再发起新请求，等待上个完成
    if (permissionLoading) {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        await delay(100);
        const stillLoading = selectPermissionLoading(permissionId)(getState());
        if (!stillLoading) {
          sessionId = selectSessionId(permissionId)(getState());
          baseUrl = selectBaseUrl(permissionId)(getState());
          authentication = selectAuthentication(permissionId)(getState());
          break;
        }
      }
    } else {
      const payload = await dispatch(fetchUploadPermission({ permissionId })).unwrap();
      sessionId = payload.sessionId;
      baseUrl = payload.baseUrl;
      authentication = payload.authentication;
    }
  }

  // 等待获取 permission 的请求可能失败（如断网），此时 baseUrl 仍为空，
  // 直接抛错让上层标记上传失败，避免向 `undefined/upload` 发起无效请求
  if (!baseUrl) {
    throw new Error('Failed to get upload permission');
  }

  if (process.env.NODE_ENV === 'development') {
    // in order to avoid CORS, convert to relative URL
    try {
      baseUrl = new URL(baseUrl).pathname;
    } catch {
      //
    }
  }

  const uploadUrl = `${baseUrl}/upload`;

  const formData = new FormData();

  for (const file of files) {
    formData.append('files', file);
  }

  return restApi
    .post(uploadUrl, formData, {
      ...config,
      headers: {
        ...config?.headers,
        Authorization: `Bearer ${authentication}`,
      },
    })
    .then((resData) => {
      return resData.map((item) => {
        return {
          // 将 sessionId 存在 resource 里，这样不管是通过表格行打开 Dialog 编辑还是使用 Page 组件在另一个
          // 表单上传文件过后再将值传会主表单，都不会丢失 sessionId
          sessionId,
          storageId: item.tempStorageId,
          fileName: item.fileName,
          url: `${baseUrl}/file/${item.tempStorageId}?jwt=${encodeURIComponent(authentication)}`,
        };
      });
    });
};

const slice = createSlice({
  name: 'fileStorage',
  initialState: { ...fileStorageState },
  reducers: {
    resetFileStorage: (state) => {
      for (const [k, v] of Object.entries(fileStorageState)) {
        if (Array.isArray(v)) {
          state[k] = [];
        }
        if (v && typeof v === 'object') {
          state[k] = {};
        }
        state[k] = v;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUploadPermission.pending, (state, action) => {
        const { permissionId } = action.meta.arg;
        if (!permissionId) {
          state.permissionLoading = true;
        } else {
          state.workspaceById[permissionId] = { permissionLoading: true };
        }
      })
      .addCase(fetchUploadPermission.fulfilled, (state, action) => {
        const { permissionId } = action.meta.arg;
        if (!permissionId) {
          state.permissionLoading = false;
          state.sessionId = action.payload.sessionId;
          state.baseUrl = action.payload.baseUrl;
          state.authentication = action.payload.authentication;
        } else {
          state.workspaceById[permissionId] = {
            permissionLoading: false,
            sessionId: action.payload.sessionId,
            baseUrl: action.payload.baseUrl,
            authentication: action.payload.authentication,
          };
        }
      })
      // 获取上传 permission 失败时必须清除 loading 标记，否则并发上传的其他文件会一直停留在
      // uploadFile 里的 while 等待循环中，既不会成功也不会失败（断网多选上传时表现为只有第一个文件被标记失败）
      .addCase(fetchUploadPermission.rejected, (state, action) => {
        const { permissionId } = action.meta.arg;
        if (!permissionId) {
          state.permissionLoading = false;
        } else if (state.workspaceById[permissionId]) {
          state.workspaceById[permissionId].permissionLoading = false;
        }
      });
  },
});

export const { resetFileStorage } = slice.actions;

export default slice.reducer;
